import { useQuery } from '@apollo/client';
import {
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Tab,
  TabGroup,
  TabGroupVariant,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { TranslationsContext } from '../../common/contexts';
import { IBooking, IBookingStatus } from '../../common/types/apiSchema';
import { ErrorKey, ErrorType } from '../../common/utils/tracking/tracking.interfaces';
import { trackError } from '../../common/utils/tracking/trackingUtils';
import { BookingCardListVariant } from '../BookingCardList';
import { BookingsPageTab } from './BookingsPage.components';
import { GET_BOOKINGS, IBookingQueryData } from './BookingsPage.queries';
import { BookingsPageHeader, BookingsPageWrapper } from './BookingsPage.styles';

const BookingsPage: React.FunctionComponent<{}> = () => {
  const { translations } = React.useContext(TranslationsContext);

  const { loading, error, data } = useQuery<IBookingQueryData>(GET_BOOKINGS, {
    context: { protectedFlashsales: true },
  });

  if (error) {
    trackError(ErrorKey.BOOKINGS_PAGE_LOAD, ErrorType.LOADING_ERROR, error.message, error);
  }

  const allBookings: IBooking[] = !loading ? data.bookings : [];
  const upcomingBookings: IBooking[] = allBookings.filter(
    booking => booking.status === IBookingStatus.UPCOMING
  );
  const pastBookings: IBooking[] = allBookings.filter(
    booking => booking.status === IBookingStatus.PAST
  );
  const cancelledBookings: IBooking[] = allBookings.filter(
    booking => booking.status === IBookingStatus.CANCELLED
  );

  return (
    <div data-testid="bookingsPage">
      {!loading && (
        <BookingsPageWrapper>
          <BookingsPageHeader>
            <ResponsiveTypography variant={ResponsiveTypographyVariant.t2Serif}>
              {i18n('account.bookings.heading', [], translations)}
            </ResponsiveTypography>
          </BookingsPageHeader>
          <TabGroup variant={TabGroupVariant.secondary}>
            <Tab
              title={i18n('bookings.upcoming', [upcomingBookings.length], translations)}
              dataAttributes={{ testid: 'upcoming-tab' }}
            >
              <BookingsPageTab
                bookings={upcomingBookings}
                variant={BookingCardListVariant.UPCOMING}
              />
            </Tab>
            <Tab
              title={i18n('bookings.past', [pastBookings.length], translations)}
              dataAttributes={{ testid: 'past-tab' }}
            >
              <BookingsPageTab bookings={pastBookings} variant={BookingCardListVariant.PAST} />
            </Tab>
            <Tab
              title={i18n('bookings.cancelled', [cancelledBookings.length], translations)}
              dataAttributes={{ testid: 'cancelled-tab' }}
            >
              <BookingsPageTab
                bookings={cancelledBookings}
                variant={BookingCardListVariant.CANCELLED}
              />
            </Tab>
          </TabGroup>
        </BookingsPageWrapper>
      )}
    </div>
  );
};

export default BookingsPage;
